import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtUSD, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Landmark } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ClientWithdrawal() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ amount: "", note: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/client/transactions", { params: { type: "withdrawal" } }).then((r) => setHistory(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/client/withdrawals", {
        amount,
        method: "Bank Wire",
        note: form.note,
      });
      toast.success("Withdrawal Processed");
      setForm({ amount: "", note: "" });
      load();
      refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border" data-testid="withdrawal-page">
      <form onSubmit={submit} className="lg:col-span-1 bg-card p-8 space-y-5">
        <div>
          <p className="label-eyebrow mb-2">Withdraw Funds</p>
          <h3 className="text-lg font-semibold">Request Withdrawal</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Available balance: <span className="text-foreground font-mono-num font-semibold" data-testid="wd-balance">{fmtUSD(user?.balance || 0)}</span>
          </p>
        </div>
        <div>
          <Label className="label-eyebrow">Amount (USD)</Label>
          <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-2 h-11 bg-background border-subtle font-mono-num" data-testid="wd-amount" placeholder="0.00" required />
        </div>
        <div>
          <Label className="label-eyebrow flex items-center gap-1.5">
            <Landmark className="w-3 h-3" /> Payout Method
          </Label>
          <div
            className="mt-2 h-11 flex items-center px-3 bg-background border border-subtle rounded text-sm font-mono-num"
            data-testid="wd-method-fixed"
          >
            Bank Wire (registered account on file)
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Note</Label>
          <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2 bg-background border-subtle" rows={2} placeholder="Optional" data-testid="wd-note" />
        </div>
        <Button type="submit" disabled={busy} className="w-full h-11" data-testid="submit-withdrawal">
          {busy ? "Submitting…" : "Submit Withdrawal Request"}
        </Button>
        <p className="text-xs text-muted-foreground border-t border-subtle pt-4">
          Funds will be sent to the bank account on file for your profile. To
          update these details, please contact your account manager.
        </p>
      </form>

      <div className="lg:col-span-2 bg-card p-8">
        <p className="label-eyebrow mb-2">History</p>
        <h3 className="text-lg font-semibold mb-4">Your Withdrawals</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-surface-hover border-subtle">
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Processed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No withdrawals yet</TableCell></TableRow>}
              {history.map((t) => (
                <TableRow key={t.id} className="border-subtle" data-testid={`my-wd-${t.id}`}>
                  <TableCell className="text-right font-mono-num font-semibold">{fmtUSD(t.amount)}</TableCell>
                  <TableCell>{t.method}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                      t.status === "pending" ? "bg-warning/15 text-warning" :
                      t.status === "approved" ? "bg-profit/15 text-profit" :
                      "bg-loss/15 text-loss"
                    }`}>{t.status.toUpperCase()}</span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(t.processed_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
